/**
 * «Orët» në Excel — një rresht për punëtor, jo një kolonë për minutë.
 *
 * Rrjeta në ekran ka shtatëdhjetë e dy kolona dhjetë-minutëshe; e kopjuar ashtu
 * te një fletë, ajo bëhet një mur numrash që s'i lexon askush dhe që Excel-i as
 * nuk e printon dot. Një dokument përgjigjet pyetjes tjetër: sa punoi secili,
 * kundrejt sa ishte caktuar, dhe ku qëndron diferenca.
 *
 * Totalet vijnë nga `sumWorkerDays` — E NJËJTA llogaritje që ushqen shiritin te
 * ekrani. Dy formula do të pajtoheshin derisa njëra të ndryshonte, dhe atëherë
 * skedari që shkon te klienti do të mospajtohej me ekranin ku u pa.
 *
 * Faqosja vjen nga `reportSheetExport`, si Lista e Pagave dhe raportet e tjera:
 * kokë kompanie, paletë e markës, rresht TOTALI, vend për nënshkrim. Gjuha vjen
 * nga thirrësi — asnjë varg i ngurtë këtu.
 *
 * Ndërtimi rri i ndarë nga shkarkimi (`buildOretSheet` kundrejt
 * `exportOretExcel`) që kolonat të provohen pa hapur asnjë skedar.
 */
import { exportSheetExcel } from './reportSheetExport';

/** Minuta → `H:MM`. Negativja e mban shenjën: diferenca ka drejtim. */
export function hm(min) {
  const v = Math.round(Number(min) || 0);
  const sign = v < 0 ? '-' : '';
  const a = Math.abs(v);
  return `${sign}${Math.floor(a / 60)}:${String(a % 60).padStart(2, '0')}`;
}

/**
 * Përshkrimi i fletës.
 *
 * @param {Array}  workers  punëtorët e filtruar, ashtu si duken në ekran
 * @param {object} opts
 *   - dayMap    { workerId: { 'YYYY-MM-DD': dita } }
 *   - dates     datat e periudhës
 *   - sum       (days, dates) → totalet e një punëtori  (`sumWorkerDays`)
 *   - period    { label, from, to }
 *   - projectName  filtri aktiv, ose bosh
 *   - company   { CompanyName, Logo }
 *   - labels    etiketat e përkthyera
 */
export function buildOretSheet(workers = [], opts = {}) {
  const { dayMap = {}, dates = [], sum, period = {}, projectName = '',
          company = {}, labels = {}, today = '' } = opts;

  const L = {
    title: 'PLANI I PUNËS — ORËT',
    worker: 'Punëtori', code: 'Kodi', shift: 'Orari',
    worked: 'Punuar', planned: 'Orë plan.', diff: 'Diferenca',
    productive: 'Produktive', pause: 'Pauzë', overtime: 'Overtime',
    late: 'Vonesë (min)', absentDays: 'Ditë mungese', workDays: 'Ditë pune',
    breakTimes: 'Pauzat (ora)',
    period: 'Periudha', project: 'Projekti', allProjects: 'Të gjithë projektet',
    workersTotal: 'Punëtorë', generated: 'Lëshuar më', total: 'TOTALI', leaves: 'Pushime (% e ditëve)',
    ...labels,
  };

  /**
   * Llojet e punës bëhen KOLONA — po vetëm ato që janë vërtet në plan.
   *
   * Fjalori ka njëzet e ca tipe; një kolonë për secilin do të jepte një fletë
   * ku njëzet janë bosh. Kolonat dalin nga vetë plani i periudhës, në rendin që
   * i sheh — dhe një periudhë pa asnjë lloj të caktuar nuk fiton asnjë kolonë.
   */
  const totals = (workers || []).map((w) => sum((dayMap[w?.Id ?? w?.id] || {}).days
    || dayMap[w?.Id ?? w?.id] || {}, dates));
  const typeCols = [];
  const seenType = new Set();
  for (const t of totals) {
    for (const [k, v] of (t.byType || new Map())) {
      if (seenType.has(k) || !v.minutes) continue;
      seenType.add(k);
      typeCols.push({ key: `wt_${k}`, mapKey: k, label: v.name, w: 13, align: 'right' });
    }
  }

  const columns = [
    { key: 'worker',     label: L.worker,     w: 26 },
    { key: 'code',       label: L.code,       w: 12, muted: true },
    { key: 'shift',      label: L.shift,      w: 14, muted: true },
    { key: 'workDays',   label: L.workDays,   w: 10, align: 'right' },
    { key: 'worked',     label: L.worked,     w: 11, align: 'right' },
    { key: 'planned',    label: L.planned,    w: 11, align: 'right' },
    { key: 'diff',       label: L.diff,       w: 11, align: 'right' },
    { key: 'productive', label: L.productive, w: 12, align: 'right' },
    ...typeCols,
    // Kur bien pauzat — teksti, jo shuma: «sa pauzë» e thotë kolona pranë,
    // kurse pyetja «kur shkon» kërkon orën.
    { key: 'breakTimes', label: L.breakTimes, w: 26, muted: true },
    { key: 'pause',      label: L.pause,      w: 10, align: 'right' },
    { key: 'overtime',   label: L.overtime,   w: 11, align: 'right' },
    { key: 'late',       label: L.late,       w: 12, align: 'right' },
    { key: 'absentDays', label: L.absentDays, w: 12, align: 'right' },
    // Pushimet sipas llojit, me % ndaj ditëve të punës (p.sh. «Mjekësor 2 (10%)»).
    { key: 'leaves',     label: L.leaves,     w: 30, muted: true },
  ];

  const acc = { workDays: 0, worked: 0, planned: 0, productive: 0,
                pause: 0, overtime: 0, late: 0, absentDays: 0 };
  const accType = new Map();
  const accLeave = new Map();
  const pct = (a, b) => (b > 0 ? `${Math.round((a / b) * 1000) / 10}%` : '—');
  const leaveText = (map, workDays) => [...(map || new Map()).values()]
    .sort((a, b) => b.days - a.days)
    .map((v) => `${v.label} ${v.days} (${pct(v.days, workDays)})`).join(' · ');

  const body = (workers || []).map((w, i) => {
    const t  = totals[i];
    for (const k of Object.keys(acc)) {
      acc[k] += k === 'pause' ? t.paused : k === 'absentDays' ? t.absentDays : (t[k] || 0);
    }
    // Orari vjen nga rreshti i PAMJES (`dayMap`), jo nga profili i punëtorit:
    // lista e punëtorëve s'i mban orët e turnit, ndaj kolona dilte gjithmonë bosh.
    const ov = dayMap[w?.Id ?? w?.id] || {};
    const st = ov.schedule_start ?? w?.schedule_start;
    const en = ov.schedule_end ?? w?.schedule_end;
    const shift = st && en ? `${String(st).slice(0, 5)}–${String(en).slice(0, 5)}` : '';
    const row = {
      worker: `${w?.FirstName || w?.first_name || ''} ${w?.LastName || w?.last_name || ''}`.trim(),
      code: w?.EmployeeCode || w?.employee_code || '',
      shift,
      workDays: t.workDays,
      worked: hm(t.worked),
      planned: hm(t.planned),
      // Diferenca mban shenjën: «-2:15» dhe «2:15» janë dy gjendje të kundërta,
      // dhe një vlerë absolute do t'i barazonte.
      diff: hm(t.worked - t.planned),
      productive: hm(t.productive),
      pause: hm(t.paused),
      overtime: hm(t.overtime),
      late: t.late,
      absentDays: t.absentDays,
      // Renditja sipas orës, që dreka të mos dalë para pauzës së mëngjesit.
      breakTimes: [...(t.breakTimes || [])].sort((a, b) => a.slice(2).localeCompare(b.slice(2))).join(' · '),
    };
    row.leaves = leaveText(t.leaves, t.workDays);
    for (const [k, v] of (t.leaves || new Map())) {
      const cur = accLeave.get(k) || { ...v, days: 0 };
      cur.days += v.days; accLeave.set(k, cur);
    }
    for (const col of typeCols) {
      const v = (t.byType || new Map()).get(col.mapKey);
      row[col.key] = hm(v?.minutes || 0);
      accType.set(col.mapKey, (accType.get(col.mapKey) || 0) + (v?.minutes || 0));
    }
    return row;
  });

  return {
    title: L.title,
    logo: company.Logo || null,
    left: [company.CompanyName || company.name || ''].filter(Boolean),
    right: [
      `${L.period}: ${period.label || ''} ${period.from || ''}${period.to && period.to !== period.from ? ` – ${period.to}` : ''}`.trim(),
      `${L.project}: ${projectName || L.allProjects}`,
      `${L.workersTotal}: ${body.length}`,
      today ? `${L.generated}: ${today}` : '',
    ].filter(Boolean),
    columns,
    body,
    totalLabel: L.total,
    totals: {
      workDays: acc.workDays,
      worked: hm(acc.worked),
      planned: hm(acc.planned),
      diff: hm(acc.worked - acc.planned),
      productive: hm(acc.productive),
      pause: hm(acc.pause),
      overtime: hm(acc.overtime),
      late: acc.late,
      absentDays: acc.absentDays,
      leaves: leaveText(accLeave, acc.workDays),
      ...Object.fromEntries(typeCols.map((c) => [c.key, hm(accType.get(c.mapKey) || 0)])),
    },
    sheetName: 'Orët',
    fileBase: `oret-${period.from || ''}`,
    orientation: 'landscape',
  };
}

export function exportOretExcel(workers, opts) {
  return exportSheetExcel(buildOretSheet(workers, opts));
}
